<template>
    <div class="flex flex-col h-full gap-2">
        <div class="flex-initial flex items-center">
            <label class="font-bold mx-1">
                Đội xe:
            </label>
            <hts-select-cooperator
                v-model="partner.id"
                v-model:name="partner.name"
                type-cooperator="PARTNER"
                :items="[{ _id: null, name: 'Tất cả', class: { 'text-blue-500': true } }]"
                :clearable="false"
                label=""
                placeholder="Vui lòng chọn đội xe"
                class="max-w-80">
                <template #prefix>
                    <hts-icons
                        icon-name="hi-magnifying-glass-icon"
                        class="text-gray-500" />
                </template>
            </hts-select-cooperator>
        </div>
        <div class="hts-monitoring-vehicle__container">
            <div class="container-left show">
                <div class="flex-initial box-border p-2">
                    <div class="w-full">
                        <hts-form-input
                            v-model="vehicleFilters.search"
                            class="w-full mt-1"
                            :placeholder="$t('labels.findVehicle')">
                            <template #prefix>
                                <hts-icons
                                    icon-name="hi-magnifying-glass-icon"
                                    class="text-gray-500" />
                            </template>
                        </hts-form-input>
                        <div>
                            <hts-filter-toggle
                                v-model="vehicleFilters.processStatus"
                                :items="vehicleStatuses"
                                label="Trạng thái vận hành"
                                class="mt-1 mr-1" />
                        </div>
                    </div>
                </div>

                <div
                    v-loading="vehicleLoading"
                    class="content overflow-hidden">
                    <div class="flex gap-2 items-center p-2 font-bold bg-white border-b">
                        <div class="flex-1">
                            Biển số
                        </div>
                        <div class="flex-1">
                            Đội xe
                        </div>
                    </div>
                    <div class="h-full relative overflow-auto">
                        <vehicle-list
                            :items="vehicles"
                            :filters="vehicleFilters"
                            info-type="partner"
                            @selected="handleSelectVehicle" />
                    </div>
                </div>
            </div>

            <div class="container-right">
                <schedule-map
                    v-if="showMap"
                    class="min-h-[40%]"
                    :show-location="showMapLocation"
                    :journey="selectedJourney" />

                <div class="journey">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center gap-1">
                            <hts-filter-toggle
                                v-model="journeyFilters.processStatus"
                                :items="statuses"
                                label="Trạng thái cảnh báo vận hành" />
                        </div>

                        <div class="flex items-center gap-3">
                            <hts-form-input
                                v-model="journeyFilters.search"
                                class="w-72"
                                :placeholder="$t('labels.findJourney')">
                                <template #prefix>
                                    <hi-magnifying-glass-icon class="h-6 w-6 ml-2 text-gray-500" />
                                </template>
                            </hts-form-input>
                            <div class="flex gap-3">
                                <hts-map-icon-location store-key="monitoringPartner" />
                                <hts-map-icon-map store-key="monitoringPartner" />
                            </div>
                        </div>
                    </div>
                    <div class="mt-3">
                        <hts-data-table
                            ref="dataTable"
                            class="w-full"
                            :custom-style="{ 'min-height': '300px' }"
                            :headers="headers"
                            :server="server"
                            :query-extend="queryExtend"
                            row-selection="single"
                            enable-cell-change-flash
                            @selection-changed="handleJourneySelectedChange"
                            @row-click="handleRowClick" />
                    </div>
                </div>
            </div>
            <journey-info
                ref="journeyInfoComponent"
                :api="$url.monitoring.journey.getDetail"
                :exclude="journeyInfoExclude"
                :travel-exclude="['start', 'stop']" />
            <alert-list-drawer ref="alertListComponent" />
        </div>
    </div>
</template>

<script setup lang="ts">
import JourneyInfo from '../-components/journey-info.vue'
import ScheduleMap from '../journey/-components/journey-schedule/map.vue'
import AlertListDrawer from './-components/alert-list-drawer.vue'
import AlertStatus from './-components/alert-status.vue'
import VehicleList from './-components/vehicle-list.vue'
import Status from '~/components/status.vue'

const { $event, $formatters } = useNuxtApp()
const { monitoringPage, system, map } = $store()
const company = computed(() => system.getCompany)
const partner = computed({
  get () {
    return (
      monitoringPage.partnerSelected || {
        id: '',
        name: ''
      }
    )
  },
  set (val: any) {
    monitoringPage.selectPartner(val)
  }
})

const showMap = computed(() => {
  return map.getShowMap.monitoringPartner
})

const showMapLocation = computed(() => {
  return map.getShowLocation.monitoringPartner
})

onBeforeMount(() => {
  map.setShowLocation({ monitoringPartner: false })
  map.setShowMap({ monitoringPartner: true })
})

const dataTable = ref<any>()
const journeyInfoComponent: any = ref(null)
const alertListComponent: any = ref(null)
const alertNumber = ref(0)
const selectedJourney = ref<any>(undefined)
const server = $url.monitoring.journey.get
const queryExtend = computed(() => {
  return {
    vehicleId: selectedVehicleId.value,
    processAlertStatus: journeyFilters.value.processStatus,
    scheduleType: journeyFilters.value.scheduleType,
    vehicleType: journeyFilters.value.vehicleType,
    search: journeyFilters.value.search,
    partnerId: partner.value?.id
  }
})
const journeyInfoExclude = computed<any[]>(() => ['plan-actual', 'tasks', 'driver-notifications', 'prices'])
const vehicleLoading = ref(false)
const vehicles = ref<any[]>([])
const vehicleLength = computed(() => vehicles.value.length)
const vehicleFilters = computed({
  get: () => monitoringPage.vehicleFilters,
  set: (val: any) => {
    monitoringPage.setVehicleFilters(val)
  }
})
const journeyFilters = computed(() => monitoringPage.journeyFilters)
const vehicleStatuses = ref([
  {
    label: 'Đang chờ',
    value: 0
  },
  {
    label: 'Đang chạy',
    value: 1
  }
])
const statuses = ref([
  {
    iconName: 'hi-truck-icon',
    iconColor: 'orange',
    label: 'Vàng',
    value: 1
  },
  {
    iconName: 'hi-truck-icon',
    iconColor: 'red',
    label: 'Đỏ',
    value: 2
  },
  {
    iconName: 'hi-truck-icon',
    iconColor: 'green',
    label: 'Xanh',
    value: 0
  }
])
const vehicleTypes = ref([
  {
    label: 'Xe container',
    value: 'container'
  },
  {
    label: 'Xe không container',
    value: 'non_container'
  }
])
const scheduleTypes = ref([
  {
    label: 'Manual',
    value: 'manual'
  },
  {
    label: 'Automation',
    value: 'auto'
  }
])

onBeforeMount(() => {
  eventListening()
})
onMounted(() => {
  const route = useRoute()
  if (route.query.journeyId) {
    openView({ _id: route.query.journeyId })
  }
  getVehicles()
})

watch(
  () => vehicleFilters.value.search,
  (val: string) => {
    getVehiclesDebounce(true)
  }
)
watch(
  () => vehicleFilters.value.processStatus,
  (val: string) => {
    getVehiclesDebounce(true)
  }
)
watch(
  () => vehicleFilters.value.vehicleType,
  (val: string) => {
    getVehiclesDebounce(true)
  }
)
watch(
  () => journeyFilters.value.search,
  (val: string) => {}
)
watch(
  () => journeyFilters.value.processStatus,
  (val: any) => {
    refreshTable()
  }
)
watch(
  () => journeyFilters.value.scheduleType,
  (val: any) => {
    refreshTable()
  }
)
watch(
  () => journeyFilters.value.vehicleType,
  (val: any) => {
    refreshTable()
  }
)
watch(
  () => journeyFilters.value.search,
  (val: any) => {
    refreshTable()
  }
)
watch(
  () => partner.value.id,
  (val: any) => {
    refreshTable()
    getVehiclesDebounce(true)
  }
)
const refreshTable = $lodash.debounce(() => {
  dataTable.value && dataTable.value.refresh()
}, 500)
const getVehicles = (clear: boolean = false) => {
  vehicleLoading.value = true
  if (clear) {
    vehicles.value = []
  }
  $api($url.monitoring.vehicle.get, {
    body: {
      skip: vehicleLength.value,
      search: vehicleFilters.value.search,
      processStatus: vehicleFilters.value.processStatus,
      vehicleType: vehicleFilters.value.vehicleType,
      partnerId: partner.value?.id
    }
  })
    .then(({ data }: any) => {
      const temp = (data?.value || {}).data || []
      if (Array.isArray(temp)) {
        vehicles.value = [...vehicles.value, ...temp]
      }
      vehicleLoading.value = false
    })
    .catch(() => {
      vehicleLoading.value = false
    })
}
const getVehiclesDebounce = $lodash.debounce(getVehicles, 500)

const eventListening = () => {
  $event.listen(`c-${company.value._id}-journey-change`, (event: any) => {
    const { message } = event || {}
    const { journeyId, changes } = message || {}
    if (journeyId) {
      journeyInfoComponent.value && journeyInfoComponent.value.refresh(journeyId)
      // dataTable.value && dataTable.value.setItem(journeyId, await getJourney(journeyId))
      alertNumber.value++
    }
  })
  $event.listen(`c-${company.value._id}-vehicle-change`, ({ id, type, location, journey, status, processStatus }: any) => {
    //
  })
}

const handleJourneySelectedChange = (journeys: any) => {
  const positions = journeys?.value?.[0]?.journey
  try {
    let check = true
    if (positions?.length) {
      if (!positions[0].lat || !positions[0].lng) {
        check = false
      }
      const origin = {
        lat: parseFloat(positions[0].lat),
        lng: parseFloat(positions[0].lng)
      }
      positions.splice(0, 1)
      if (!positions[positions.length - 1].lat || !positions[positions.length - 1].lng) {
        check = false
      }
      const destination = {
        lat: parseFloat(positions[positions.length - 1].lat),
        lng: parseFloat(positions[positions.length - 1].lng)
      }
      positions.splice(positions.length - 1, 1)
      const waypoints: { location: google.maps.LatLngLiteral }[] = []
      positions.forEach((position: any) => {
        if (!position.lat || !position.lng) {
          check = false
        }
        const waypoint = {
          location: {
            lat: parseFloat(position.lat),
            lng: parseFloat(position.lng)
          }
        }
        waypoints.push(waypoint)
      })
    }
  } catch (e) {}
}

const handleRowClick = (data: any) => {
  if (selectedJourney.value?._id === data._id) {
    return
  }
  selectedJourney.value = data
}

const openView = (row: any) => {
  journeyInfoComponent.value && journeyInfoComponent.value.show(row._id)
}

const selectedVehicleId = ref<any>(null)
const handleSelectVehicle = (vehicle: any) => {
  if (!vehicle || !vehicle._id) {
    selectedVehicleId.value = null
    refreshTable()
    return
  }
  selectedVehicleId.value = vehicle._id
  refreshTable()
}

const headers = ref([
  {
    field: 'processAlertStatus',
    headerName: '',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    suppressMenu: true,
    suppressMovable: true,
    resizable: false,
    minWidth: 85,
    maxWidth: 85,
    cellClass: 'cell-no-padding',
    cellRenderer: AlertStatus
  },
  {
    field: 'code',
    headerName: $t('labels.code'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'journeyName',
    headerName: $t('labels.journey2')
  },
  {
    field: 'partnerName',
    headerName: $t('labels.partner')
  },
  {
    field: 'status',
    headerName: $t('labels.status'),
    cellRenderer: Status,
    cellRendererParams: {
      module: 'journeyStatus',
      label: '',
      keyStatus: 'status'
    }
  },
  {
    field: 'transportation',
    sortable: true,
    headerName: $t('labels.transportation'),
    valueGetter: (params: any) => {
      const transportation = params?.data.orderData?.transportation
      if (transportation === 'CONTAINER') {
        return $t('labels.containerTruck')
      }
      if (transportation === 'NON_CONTAINER') {
        return $t('labels.nonContainerTruck')
      }
    }
  },
  {
    field: 'mergeContainer',
    headerName: $t('labels.mergeContainer'),
    valueGetter: (params: any) => {
      const mergeContainer = params?.data?.orderData?.mergeContainer
      if (mergeContainer) {
        return $t('labels.haveMergeContainer')
      }
      return $t('labels.notMergeContainer')
    }
  },
  {
    field: 'headContainer',
    headerName: $t('labels.headContainer'),
    valueGetter: (params: any) => {
      const headContainer = params?.data?.orderData?.headContainer
      if (headContainer) {
        return $t('labels.true')
      }
      return $t('labels.false')
    }
  },
  {
    field: 'commodityName',
    headerName: $t('labels.commodity'),
    valueGetter: (params: any) => {
      const commodityName = params?.data?.orderData?.commodityName
      const commodityCategory = params?.data?.orderData?.commodityCategory
      if (commodityName && commodityCategory) {
        return `${commodityName} - ${commodityCategory}`
      }
      if (commodityName) {
        return commodityName
      }
      return ''
    }
  },
  {
    field: 'etd',
    headerName: $t('labels.etd'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.startAt)
    }
  },
  {
    field: 'eta',
    headerName: $t('labels.eta'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.stopAt)
    }
  },
  {
    field: 'note',
    headerName: $t('labels.note')
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    suppressMenu: true,
    suppressMovable: true,
    resizable: false,
    headerName: $t('labels.action'),
    headerClass: 'ag-header-text-center',
    cellStyle: { overflow: 'visible' },
    cellRenderer: 'DataTableActions',
    cellRendererParams: {
      storeKey: 'view',
      storeName: 'view',
      items: [
        {
          icon: 'hi-eye-icon',
          label: $t('labels.view'),
          class: 'text-blue-600 hover:text-blue-500',
          click: openView
        }
      ]
    }
  }
])
</script>
